import { dateToTimeStamp } from "../../tools/timeTool";

// private data
const _daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const _weekdayLabels = [
	"Lundi",
	"Mardi",
	"Mercredi",
	"Jeudi",
	"Vendredi",
	"Samedi",
	"Dimanche",
];
const _monthLabels = [
	"Janvier",
	"Février",
	"Mars",
	"Avril",
	"Mai",
	"Juin",
	"Juillet",
	"Août",
	"Septembre",
	"Octobre",
	"Novembre",
	"Décembre",
];
const _today = new Date();
const _todayComps = {
	year: _today.getFullYear(),
	month: _today.getMonth() + 1,
	day: _today.getDate(),
};

/*************************************
 State
 **/
const state = {
	month: _todayComps.month,
	year: _todayComps.year,
	currentDay: _todayComps,
	mainCalendarIsVisible: false,
	today: null,
	now: new Date(),
};

/*************************************
 Getters
 **/
const getters = {
	getToday: (state) => {
		return {
			year: state.now.getFullYear(),
			month: state.now.getMonth() + 1,
			day: state.now.getDate(),
			seconds: state.now.getSeconds(),
			minutes: state.now.getMinutes(),
			hour: state.now.getHours(),
		};
	},
	getYear: (state) => {
		return state.year;
	},
	getMonth: (state) => {
		return state.month;
	},
	getYearToday: (state, getters) => {
		return getters["getToday"].year;
	},
	getMonthToday: (state, getters) => {
		return getters["getToday"].month;
	},
	getFiveCurrentMonths: (state, getters) => {
		let currentIndex = _monthLabels.findIndex(function (val) {
			return val === _monthLabels[state.month - 1];
		});

		let fiveMonths = [];
		let mId0 = currentIndex - 2;
		let y0 = state.year;
		let mId1 = currentIndex - 1;
		let y1 = state.year;

		if (currentIndex === 0) {
			mId0 = _monthLabels.length - 2;
			y0 = state.year - 1;
			mId1 = _monthLabels.length - 1;
			y1 = state.year - 1;
		} else if (currentIndex === 1) {
			mId0 = _monthLabels.length - 1;
			y0 = state.year - 1;
		}

		fiveMonths[0] = {
			label: _monthLabels[mId0],
			id: mId0,
			selected: getters["getToday"].month - 1 === mId0,
			active: false,
			year: y0,
		};

		fiveMonths[1] = {
			label: _monthLabels[mId1],
			id: mId1,
			selected: getters["getToday"].month - 1 === mId1,
			active: false,
			year: y1,
		};

		fiveMonths[2] = {
			label: _monthLabels[currentIndex],
			id: currentIndex,
			selected: getters["getToday"].month - 1 === currentIndex,
			active: true,
			year: state.year,
		};

		let mId3 = currentIndex + 1;
		let y3 = state.year;
		let mId4 = currentIndex + 2;
		let y4 = state.year;

		if (currentIndex === _monthLabels.length - 1) {
			mId3 = 0;
			y3 = state.year + 1;
			mId4 = 1;
			y4 = state.year + 1;
		} else if (currentIndex === _monthLabels.length - 2) {
			mId4 = 0;
			y4 = state.year + 1;
		}

		fiveMonths[3] = {
			label: _monthLabels[mId3],
			id: mId3,
			selected: getters["getToday"].month - 1 === mId3,
			active: false,
			year: y3,
		};

		fiveMonths[4] = {
			label: _monthLabels[mId4],
			id: mId4,
			selected: getters["getToday"].month - 1 === mId4,
			active: false,
			year: y4,
		};

		return fiveMonths;
	},
	// Our component exposes month as 1-based, but sometimes we need 0-based
	getMonthIndex: (state) => {
		return state.month - 1;
	},
	getIsLeapYear: (state) => {
		return (
			state.year % 4 === 0 && (state.year % 100 !== 0 || state.year % 400 === 0)
		);
	},
	// Day/month/year components for previous month
	getPreviousMonthComps: (state, getters) => {
		if (state.month === 1) {
			return {
				days: _daysInMonths[11],
				month: 12,
				year: state.year - 1,
			};
		}
		return {
			days:
				state.month === 3 && getters["getIsLeapYear"]
					? 29
					: _daysInMonths[state.month - 2],
			month: state.month - 1,
			year: state.year,
		};
	},
	// Day/month/year components for next month
	getNextMonthComps: (state, getters) => {
		if (state.month === 12) {
			return {
				days: _daysInMonths[0],
				month: 1,
				year: state.year + 1,
			};
		}
		return {
			days:
				state.month === 2 && getters["getIsLeapYear"]
					? 29
					: _daysInMonths[state.month],
			month: state.month + 1,
			year: state.year,
		};
	},
	// State for calendar header (no dependencies yet...)
	getMonths: () => {
		return _monthLabels.map((ml, i) => ({
			label: ml,
			label_1: ml.substring(0, 1),
			label_2: ml.substring(0, 2),
			label_3: ml.substring(0, 3),
			number: i + 1,
		}));
	},
	// State for weekday header (no dependencies yet...)
	getWeekdays: () => {
		return _weekdayLabels.map((wl, i) => ({
			label: wl,
			label_1: wl.substring(0, 1),
			label_2: wl.substring(0, 2),
			label_3: wl.substring(0, 3),
			number: i + 1,
		}));
	},
	// Returns number for first weekday (1-7), starting from Monday
	getFirstWeekdayInMonth: (state, getters) => {
		return new Date(state.year, getters["getMonthIndex"], 0).getDay() + 1;
	},
	// Returns number of days in the current month
	getDaysInMonth: (state, getters) => {
		// Check for February in a leap year
		if (state.month === 2 && getters["getIsLeapYear"]) return 29;
		// ...Just a normal month
		return _daysInMonths[getters["getMonthIndex"]];
	},
	getCurrentDateLinearUnShorted: (state, getters) => {
		if (state.currentDay.labelDay === undefined) return "";
		let shortDay = state.currentDay.labelDay.toString();
		let shortMonth = state.currentDay.labelMonth.toString();
		return (
			`${shortDay} ${state.currentDay.day} ${shortMonth} ${state.currentDay.year}`
		);
	},
	getCurrentDateLinear: (state, getters) => {
		if (state.currentDay.labelDay === undefined) return "";
		let shortDay = state.currentDay.labelDay.toString().substring(0, 3);
		let shortMonth = state.currentDay.labelMonth.toString();
		let dayLeadingZero = (`0${state.currentDay.day}`).slice(-2);
		return (
			`${shortDay}. ${dayLeadingZero} ${shortMonth} ${state.currentDay.year}`
		);
	},
	getCurrentDateLinearShort: (state, getters) => {
		if (state.currentDay.labelDay === undefined) return "";
		let shortDay = state.currentDay.labelDay.toString().substring(0, 3);
		let dayLeadingZero = (`0${state.currentDay.day}`).slice(-2);
		if (parseInt(shortDay) < 10) shortDay = `0${shortDay}`;
		let shortMonth = state.currentDay.month;
		if (parseInt(shortMonth) < 10) shortMonth = `0${shortMonth}`;
		let shortYear = state.currentDay.year.toString().substring(2, 4);
		return (
			`${shortDay}. ${dayLeadingZero}/${shortMonth}/${shortYear}`
		);
	},
	getCurrentTimestamp: (state) => {
		// -1 on month trick for match with Date object who start to 0
		let d = dateToTimeStamp(
			state.currentDay.day,
			state.currentDay.month - 1,
			state.currentDay.year,
		);
		return d;
	},
	getTodayTimestamp: (state, getters) => {
		// -1 on month trick for match with Date object who start to 0
		let d = dateToTimeStamp(
			getters["getToday"].day,
			getters["getToday"].month - 1,
			getters["getToday"].year,
		);
		return d;
	},
	getDateToTimestamp: (state) => (date) => {
		// -1 on month trick for match with Date object who start to 0
		let d = dateToTimeStamp(date.day, date.month - 1, date.year);
		return d;
	},
	getWeeks: (state, getters) => {
		const weeks = [];
		let previousMonth = true;
		let thisMonth = false;
		let nextMonth = false;
		let previousMonthComps = getters["getPreviousMonthComps"];
		let firstWeekdayInMonth = getters["getFirstWeekdayInMonth"];
		let daysInMonth = getters["getDaysInMonth"];
		let nextMonthComps = getters["getNextMonthComps"];
		let day = previousMonthComps.days - firstWeekdayInMonth + 2;
		let month = previousMonthComps.month;
		let year = previousMonthComps.year;
		// Cycle through each week of the month, up to 6 total
		for (let w = 1; w <= 6 && !nextMonth; w++) {
			// Cycle through each weekday
			const week = [];
			for (let d = 1; d <= 7; d++) {
				// We need to know when to start counting actual month days
				if (previousMonth && d >= firstWeekdayInMonth) {
					// Reset day/month/year counters
					day = 1;
					month = state.month;
					year = state.year;
					// ...and flag we're tracking actual month days
					previousMonth = false;
					thisMonth = true;
				}

				// Append day info for the current week
				// Note: this might or might not be an actual month day
				//  We don't know how the UI wants to display various days,
				//  so we'll supply all the data we can
				let tmpDay = {
					label: day && thisMonth ? day.toString() : "",
					day,
					weekday: d,
					week: w,
					month,
					year,
					date: new Date(year, month - 1, day),
					beforeMonth: previousMonth,
					afterMonth: nextMonth,
					inMonth: thisMonth,
					labelDay: _weekdayLabels[d - 1],
					labelMonth: _monthLabels[month - 1],
					isToday:
						day === getters["getToday"].day &&
						month === getters["getToday"].month &&
						year === getters["getToday"].year,
					isSelected:
						day === state.currentDay.day &&
						month === state.currentDay.month &&
						year === state.currentDay.year,
					isFirstDay: thisMonth && day === 1,
					isLastDay: thisMonth && day === daysInMonth,
				};
				week.push(tmpDay);

				// We've hit the last day of the month
				if (thisMonth && day >= daysInMonth) {
					thisMonth = false;
					nextMonth = true;
					day = 1;
					month = nextMonthComps.month;
					year = nextMonthComps.year;
					// Still in the middle of the month (hasn't ended yet)
				} else {
					day++;
				}
			}
			// Append week info for the month
			weeks.push(week);
		}
		return weeks;
	},
	getCalendarVisibility: (state) => {
		return state.mainCalendarIsVisible;
	},
	getSelectedDay: function (state) {
		return state.currentDay;
	},
	getLabelDay: (state) => (dId) => {
		return _weekdayLabels[dId];
	},
	getLabelMonth: (state) => (mId) => {
		return _monthLabels[mId];
	},
	selectedDayIsToday: function (state, getters) {
		if (
			state.currentDay.day === getters["getToday"].day &&
			state.currentDay.month === getters["getToday"].month &&
			state.currentDay.year === getters["getToday"].year
		)
			return 0;
		else if (
			state.currentDay.month <= getters["getToday"].month &&
			state.currentDay.year <= getters["getToday"].year &&
			state.currentDay.day < getters["getToday"].day
		)
			return -1;
		else if (
			state.currentDay.month < getters["getToday"].month ||
			state.currentDay.year < getters["getToday"].year
		)
			return -1;
		return 1;
	},
	getDayInfos: (state, getters) => (date) => {
		let weeks = getters["getWeeks"];
		let d = {};
		weeks.forEach(function (week) {
			week.forEach(function (day) {
				if (
					day.day === date.day &&
					day.month === date.month &&
					day.year === date.year
				) {
					d = day;
				}
			});
		});
		return d;
	},
};

/*************************************
 Mutations
 **/
const mutations = {
	setYear(state, year) {
		state.today = year;
	},
	setMonth(state, month) {
		state.today = month;
	},
	setSelectedDay(state, day) {
		state.currentDay = day;
	},
	setDateTo(state, date) {
		let dayToSet = {
			year: date.getFullYear(),
			month: date.getMonth() + 1,
			day: date.getDate(),
		};
		state.year = dayToSet.year;
		state.month = dayToSet.month;
		state.currentDay = dayToSet;
	},
	setToday(state, day) {
		state.today = day;
	},
	setVisibilityCalendar(state, bool) {
		state.mainCalendarIsVisible = bool;
	},
	resetStore(state) {
		let today = new Date();
		let todayComps = {
			year: today.getFullYear(),
			month: today.getMonth() + 1,
			day: today.getDate(),
		};
		state.month = todayComps.month;
		state.year = todayComps.year;
		state.currentDay = todayComps;
		state.mainCalendarIsVisible = false;
		state.today = null;
	},
	updateNow(state) {
		state.now = new Date();
	},
};

/*************************************
 Action
 **/
const actions = {
	initDay: function ({ state, rootState, commit, dispatch }) {
		// each minute update current day
		setInterval(() => {
			commit("updateNow");
		}, 1000 * 60);
		// set current day @ today
		return new Promise((resolve, reject) => {
			dispatch("moveToThisMonthAndDay").then((val) => {
				resolve(val);
			});
		});
	},
	setSelectedDayTo: function (
		{ state, rootState, commit, dispatch, getters },
		payload,
	) {
		if (payload.month !== state.month) {
			state.month = payload.month;
		}
		if (payload.year !== state.year) {
			state.year = payload.year;
		}
		dispatch("setSelectDayTo", payload).then(() => {
			dispatch(
				"newServices/findSelectedServiceByDay",
				{
					date: getters["getSelectedDay"],
					init: false,
					previous: false,
					keepService: true,
				},
				{ root: true },
			);
		});
	},
	moveToThisMonthAndDay: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
	}) {
		return new Promise((resolve, reject) => {
			state.month = getters["getToday"].month;
			state.year = getters["getToday"].year;
			state.selectedDate = getters["getToday"];
			dispatch("setSelectDayTo", {
				day: getters["getToday"].day,
				month: getters["getToday"].month,
				year: getters["getToday"].year,
			}).then((val) => {
				resolve(val);
			});
		});
	},
	moveToNextMonth: function ({ state, rootState, commit, dispatch, getters }) {
		const { month, year } = getters["getNextMonthComps"];
		state.month = month;
		state.year = year;
		dispatch("newReservations/syncOccupationOnly", {}, { root: true });
	},
	moveToSelectedMonth: function (
		{ state, rootState, commit, dispatch, getters },
		payload,
	) {
		if (state.month > 4 && payload < 2) {
			state.month = payload + 1;
			state.year += 1;
		} else if (state.month < 9 && payload > 9) {
			state.month = payload + 1;
			state.year -= 1;
		} else {
			state.month = payload + 1;
		}
		dispatch("newReservations/syncOccupationOnly", {}, { root: true });
	},
	moveToPreviousMonth: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
	}) {
		const { month, year } = getters["getPreviousMonthComps"];
		state.month = month;
		state.year = year;
		dispatch("newReservations/syncOccupationOnly", {}, { root: true });
	},
	moveToNextYear: function ({ state, rootState, commit, dispatch, getters }) {
		state.year += 1;
		dispatch("newReservations/syncOccupationOnly", {}, { root: true });
	},
	moveToPreviousYear: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
	}) {
		state.year -= 1;
		dispatch("newReservations/syncOccupationOnly", {}, { root: true });
	},
	moveToNextDay: function (
		{ state, rootState, commit, dispatch, getters },
		keepS,
	) {
		return new Promise((resolve, reject) => {
			if (state.month !== state.currentDay.month)
				state.month = state.currentDay.month;
			if (state.year !== state.currentDay.year)
				state.year = state.currentDay.year;
			if (state.currentDay.isLastDay) {
				const { month, year } = getters["getNextMonthComps"];
				state.month = month;
				state.year = year;
				dispatch("setSelectDayTo", { day: 1, month: month, year: year }).then(
					() => {
						dispatch(
							"newServices/findSelectedServiceByDay",
							{
								date: getters["getSelectedDay"],
								init: false,
								previous: false,
								keepService: keepS,
							},
							{ root: true },
						).then(() => {
							resolve();
						});
					},
				);
			} else {
				dispatch("setSelectDayTo", {
					day: state.currentDay.day + 1,
					month: state.currentDay.month,
					year: state.currentDay.year,
				}).then(() => {
					dispatch(
						"newServices/findSelectedServiceByDay",
						{
							date: getters["getSelectedDay"],
							init: false,
							previous: false,
							keepService: keepS,
						},
						{ root: true },
					).then(() => {
						resolve();
					});
				});
			}
		});
	},
	moveToPreviousDayCheckBack: async function (
		{ state, getters, rootGetters, dispatch },
		keepS,
	) {
		// Check if the current month and year match the current day
		if (state.month !== state.currentDay.month) {
			state.month = state.currentDay.month;
		}
		if (state.year !== state.currentDay.year) {
			state.year = state.currentDay.year;
		}

		let { days, month, year } = getters["getPreviousMonthComps"];
		// If the current day is not the first day of the month, use its values for days, month, and year
		if (!state.currentDay.isFirstDay) {
			days = state.currentDay.day - 1;
			month = state.currentDay.month;
			year = state.currentDay.year;
		}

		const todayTimestamp = parseInt(getters["getTodayTimestamp"]);
		const previousDayTimestamp = parseInt(
			getters["getDateToTimestamp"]({ day: days, month: month, year: year }),
		);

		// Check if the previous day is not in the future
		if (todayTimestamp <= previousDayTimestamp) {
			let daysInfos = getters["getDayInfos"]({
				day: days,
				month: month,
				year: year,
			});
			let isOccupied = rootGetters["newServices/getMainServicesForDay"](
				daysInfos,
				true,
			);

			// Loop until an occupied day is found
			while (isOccupied.length === 0) {
				if (days === 1) {
					// If the current day is the first day of the month, move to the previous month
					const {
						days: d,
						month: m,
						year: y,
					} = getters["getPreviousMonthComps"];
					month = m;
					year = y;
					days = d;
				} else {
					// Move to the previous day
					days = days - 1;
				}
				daysInfos = getters["getDayInfos"]({
					day: days,
					month: month,
					year: year,
				});
				isOccupied = rootGetters["newServices/getMainServicesForDay"](
					daysInfos,
					true,
				);
			}
			state.month = month;
			state.year = year;
			// Set the selected day and find the selected service for that day
			await dispatch("setSelectDayTo", { day: days, month: month, year: year });
			await dispatch(
				"newServices/findSelectedServiceByDay",
				{
					date: getters["getSelectedDay"],
					init: false,
					previous: true,
					keepService: keepS,
				},
				{ root: true },
			);
		}
	},
	moveToPreviousDay: function (
		{ state, rootState, commit, dispatch, getters },
		keepS,
	) {
		return new Promise((resolve, reject) => {
			if (state.month !== state.currentDay.month)
				state.month = state.currentDay.month;
			if (state.year !== state.currentDay.year)
				state.year = state.currentDay.year;
			if (state.currentDay.isFirstDay) {
				const { days, month, year } = getters["getPreviousMonthComps"];
				state.month = month;
				state.year = year;
				dispatch("setSelectDayTo", {
					day: days,
					month: month,
					year: year,
				}).then(() => {
					dispatch(
						"newServices/findSelectedServiceByDay",
						{
							date: getters["getSelectedDay"],
							init: false,
							previous: true,
							keepService: keepS,
						},
						{ root: true },
					).then(() => {
						resolve();
					});
				});
			} else {
				dispatch("setSelectDayTo", {
					day: state.currentDay.day - 1,
					month: state.currentDay.month,
					year: state.currentDay.year,
				}).then((d) => {
					dispatch(
						"newServices/findSelectedServiceByDay",
						{
							date: getters["getSelectedDay"],
							init: false,
							previous: true,
							keepService: keepS,
						},
						{ root: true },
					).then(() => {
						resolve();
					});
				});
			}
		});
	},
	moveToNDayAfterCurrent: function (
		{ state, rootState, commit, dispatch, getters },
		jump,
	) {
		return new Promise((resolve, reject) => {
			let current = getters["getSelectedDay"];
			let jumpDif = jump + current.day;
			while (getters["getDaysInMonth"] < jumpDif) {
				jumpDif -= getters["getDaysInMonth"];
				dispatch("moveToNextMonth");
			}
			dispatch("setSelectDayTo", {
				day: jumpDif,
				month: state.month,
				year: state.year,
			}).then((val) => {
				resolve(val);
			});
		});
	},
	showVisibilityCalendar: function ({ state }, payload = true) {
		if (payload === false) state.mainCalendarIsVisible = false;
		else {
			if (state.mainCalendarIsVisible) state.mainCalendarIsVisible = false;
			else state.mainCalendarIsVisible = true;
		}
	},
	setSelectDayTo: function (
		{ state, rootState, commit, dispatch, getters },
		date,
	) {
		return new Promise((resolve, reject) => {
			let weeks = getters["getWeeks"];
			let d = {};
			weeks.forEach(function (week) {
				week.forEach(function (day) {
					if (
						day.day === date.day &&
						day.month === date.month &&
						day.year === date.year
					) {
						d = day;
						commit("setSelectedDay", day);
					}
				});
			});
			resolve(d);
		});
	},
};

/*************************************
 Helpers
 **/

/*************************************
 Export module
 **/
export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
